import { h, Fragment, JSX } from 'preact'
import { Message } from '../../../../lib/intl/Message'
import { Parti, Geografi, PartiResultat } from '../../../domain'
import { Ubesvart } from './Ubesvart/Ubesvart'
import { useState } from 'preact/hooks'
import { PastandModul } from './Pastand'

import { Icon } from '../../shared/Icons/Icons'

import cx from 'classnames'
import tableStyles from '../Oppslutning/shared/Table.css'
import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'
import { useWidgetUrls } from '../../../routes/widget'
import { idFrom } from '../../../api'

type Props = {
  grunndata: Geografi
  partier: PartiResultat[]
  pastander: any
  widgetFooter?: JSX.Element
}

const CoreToggle = props => h('core-toggle', props)

function _Pastander({ grunndata, partier, pastander, widgetFooter }: Props) {
  const [showMore, toggleShowMore] = useState(false)
  const { pastanderUrl } = useWidgetUrls()
  const hasEndring = partier.every(p => p.stemmer.endring !== undefined)

  if (!pastander) {
    return null
  }

  const sortertePartier = [...partier].sort((a, b) => {
    return (b.stemmer.prosent || 0) - (a.stemmer.prosent || 0)
  })

  const vinner = sortertePartier[0].parti

  const oktMest = [...partier].sort((a, b) => {
    return (
      ((b.stemmer.endring && b.stemmer.endring.samme) || 0) -
      ((a.stemmer.endring && a.stemmer.endring.samme) || 0)
    )
  })[0].parti

  const mappedVinner = mapParti(vinner, pastander)
  const mappedOktMest = mapParti(oktMest, pastander)
  const mappedResten = partier
    .filter(
      p =>
        p.parti.id !== vinner.id &&
        p.parti.id !== oktMest.id &&
        p.parti.id !== 'BLANKE' &&
        sortertePartier
    )
    .slice(0, 5)
    .map(p => {
      return mapParti(p.parti, pastander)
    })

  const vinnerPastanderStasted = mappedVinner.pastandsvar
    ? mapPastander(mappedVinner, pastander)
    : null
  const oktMestPastanderStasted = mappedOktMest.pastandsvar
    ? mapPastander(mappedOktMest, pastander)
    : null
  const restenPastanderStasted = mappedResten.map(p => mapPastander(p, pastander))

  return (
    <section
      data-widget-url={pastanderUrl(grunndata, 'kommune')}
      data-valgtype={'kommune'}
      data-grunndata={idFrom(grunndata)}
      data-modulnavn="pastander"
    >
      <Message as="h3" id="hva-mener-partiene-tittel" values={{ kommune: grunndata.navn }} />
      {vinner !== oktMest && hasEndring && (
        <Message
          as="p"
          id="hva-mener-partiene-forklaring"
          values={{ vinnerParti: vinner.navn, oktMestParti: oktMest.navn, sted: grunndata.navn }}
        />
      )}
      {vinner === oktMest && hasEndring && (
        <Message
          as="p"
          id="hva-mener-partiene-forklaring-partilikhet"
          values={{ vinnerParti: vinner.navn, sted: grunndata.navn }}
        />
      )}
      {!hasEndring && (
        <Message
          id="hva-mener-partiene-forklaring-uten-endring"
          values={{ vinnerParti: vinner.navn, sted: grunndata.navn }}
        />
      )}

      {vinnerPastanderStasted ? (
        <PastandModul parti={mappedVinner} pastander={vinnerPastanderStasted} />
      ) : (
        <Ubesvart parti={vinner} />
      )}

      {hasEndring && vinner !== oktMest && oktMestPastanderStasted ? (
        <PastandModul parti={mappedOktMest} pastander={oktMestPastanderStasted} />
      ) : null}
      {!oktMestPastanderStasted && vinner !== oktMest ? <Ubesvart parti={oktMest} /> : null}

      {mappedResten.length > 0 ? (
        <Fragment>
          <CoreToggle id="showMore" hidden>
            <Message
              as="p"
              id="hva-mener-partiene-vis-fler-info"
              values={{ kommune: grunndata.navn }}
            />
            {restenPastanderStasted.map((p, index) =>
              p.length > 0 ? (
                <PastandModul
                  parti={mappedResten[index]}
                  pastander={restenPastanderStasted[index]}
                />
              ) : (
                <Ubesvart parti={mappedResten[index]} />
              )
            )}
          </CoreToggle>
          <div class={tableStyles.table__footer} style={{ margin: '0' }}>
            <button
              class={cx('nrk-button', tableStyles.table__footerButton)}
              for="showMore"
              onClick={() => toggleShowMore(!showMore)}
            >
              {!showMore ? (
                <span>
                  <Message id="lenker-vis-alle--parti" />
                  <Icon type="nrk-chevron-down" />
                </span>
              ) : (
                <span>
                  <Message id="lenker-vis-færre" />
                  <Icon type="nrk-chevron-up" />
                </span>
              )}
            </button>
          </div>
        </Fragment>
      ) : null}
      {widgetFooter}
    </section>
  )
}

export function mapParti(parti, pastander) {
  const pastandsvar = pastander.map((pastand, pastandId) => {
    const { stasted } = pastand.stasteder.find(staasted => staasted.partiId === parti.id)
    return { pastandId, stasted }
  })

  return {
    pastandsvar,
    navn: parti.navn,
    farge: parti.farge,
    fargeSvak: parti.fargeSvak
  }
}

export function mapPastander(parti, pastander) {
  try {
    return parti.pastandsvar
      .filter(pastandSvar => pastandSvar.stasted === -2 || pastandSvar.stasted === 2)
      .map(lokalPastand => {
        const match = {
          ...pastander.find((pastandSvar, pastandId) => pastandId === lokalPastand.pastandId)
        }
        match.stasted = lokalPastand.stasted
        return match
      })
  } catch (e) {
    // TODO: Håndtere feil i påstandsmapping
    return []
  }
}

export const Pastander = withSectionErrorBoundary(_Pastander)
